import { useSession, useUser } from "@clerk/react";
import { useEffect, useMemo, useState } from "react";
import { getMyConnections as getUserMembersApi } from "@/api/users";
import { Button, Chip, InputGroup, ScrollShadow } from "@heroui/react";
import { Check, Plus } from "lucide-react";
import MemberAvatar from "@/components/members/member-avatar";
import CreateMemberDrawer from "@/components/drawers/create-member-drawer";

const ParticipantsStep = ({ formData, updateFormData }) => {
  const { session } = useSession();
  const { user } = useUser();

  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const participants = formData.participants ?? [];

  useEffect(() => {
    if (!session) return;
    getMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  async function getMembers() {
    setLoading(true);
    try {
      const token = await session.getToken();
      const _resp = await getUserMembersApi(token);
      if (_resp.data.Result === "OK") {
        const records = (_resp.data.Records || []).filter(
          (rec) => String(rec.id) !== String(user?.id)
        );
        setAllUsers(records);
      } else {
        setAllUsers([]);
      }
    } catch {
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  }

  // Drives selected/unselected rendering — derived from formData, not local state.
  const selectedIds = useMemo(
    () => new Set(participants.map((p) => String(p.id))),
    [participants]
  );

  const filteredUsers = useMemo(() => {
    if (loading) return [];
    if (!search.trim()) return allUsers;
    const q = search.toLowerCase();
    return allUsers.filter(
      (u) => u.fullname?.toLowerCase().includes(q) || u.email_address?.toLowerCase().includes(q)
    );
  }, [allUsers, search, loading]);

  const toggleMember = (member) => {
    const id = String(member.id);
    const isSelected = selectedIds.has(id);
    const next = isSelected
      ? participants.filter((p) => String(p.id) !== id)
      : [...participants, member];
    updateFormData({ participants: next });
  };

  function onCreateMemberDrawerResp(_cmd) {
    if (_cmd === "close") {
      setShowCreate(false);
      getMembers();
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <InputGroup fullWidth className={"border border-accent/20 bg-accent/8 dark:bg-[#FFFFFF21] dark:border-[#FFFFFF26] h-9"}>
        <InputGroup.Input
          placeholder="Search people..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={' dark:border-[#FFFFFF26]'}
        />
        <InputGroup.Suffix>
          <Button
            isIconOnly
            size="sm"
            className={"bg-accent-soft text-accent dark:bg-[#FFFFFF21] h-6 w-6"}
            onClick={() => setShowCreate(true)}
          >
            <Plus size={16} strokeWidth={1} className="text-accent dark:text-white" />
          </Button>
        </InputGroup.Suffix>
      </InputGroup>

      {participants.length > 0 && (
        <ScrollShadow hideScrollBar className="w-full flex py-0.5" orientation="horizontal">
          {participants.map((member) => (
            <button type="button" key={member.id} onClick={() => toggleMember(member)} className="shrink-0">
              <Chip className="me-1 bg-accent/15 font-meduim border-accent border-1 text-accent dark:bg-[#FFFFFF2B] dark:text-white dark:border-white dark:border-1 flex items-center gap-1">
                <span className="capitalize">{member.fullname?.split(" ")[0]}</span>
                <Check size={12} />
              </Chip>
            </button>
          ))}
        </ScrollShadow>
      )}

      <div className="flex flex-col gap-2">
        {loading && <p className="text-sm text-muted text-center py-4">Loading people...</p>}

        {!loading && filteredUsers.length === 0 && (
          <p className="text-sm text-muted text-center py-4">No matches found.</p>
        )}

        {!loading &&
          filteredUsers.map((person) => {
            const isSelected = selectedIds.has(String(person.id));
            return (
              <button
                type="button"
                key={person.id}
                onClick={() => toggleMember(person)}
                className={[
                  "flex gap-2 items-center px-2.5 py-3 rounded-2xl border border-field-border transition-colors text-left",
                  isSelected ? "bg-accent/10 border-accent/30 dark:bg-white/8 dark:border-white/40" : "bg-surface border-accent/10",
                ].join(" ")}
              >
                <MemberAvatar member={person} color={"success"} />
                <span className="font-bold capitalize text-sm grow">{person.fullname}</span>
                {isSelected && <Check size={16} className="text-accent dark:text-white shrink-0" />}
              </button>
            );
          })}
      </div>
      <CreateMemberDrawer show = {showCreate} onResp={onCreateMemberDrawerResp}/>
    </div>
  );
};

export default ParticipantsStep;